import {useMemo} from "react";
import Select from "react-select";
import {CustomSelectStyles} from "../../constants/index.js";
import {useDarkMode} from "../../utils/darkModeContext.js";

const CustomSelect = ({
    label,
    options,
    placeholder = "Select...",
    value,
    onChange,
    margin = "",
    isRequired = false,
    showRequiredIndicator = true,
    error,
    setError,
    fieldName,
    styles,
    disabled = false,
    menuPlacement = "auto",
}) => {
    const { isDarkMode } = useDarkMode();
    
    // Derived state - find the option that matches the value
    const selectedOption = useMemo(() => {
        if (!value || !options) return null;
        return options.find(opt => opt.value === value);
    }, [value, options]);
    
    const handleChange = (selected) => {
        onChange && onChange(selected || null);
        
        // Clear error if value is selected and error exists
        if (selected && error && setError && fieldName) {
            setError(prev => {
                const newErrors = {...prev};
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    // Custom styles that match InputField placeholder styling
    const customStyles = {
        ...CustomSelectStyles,
        placeholder: (provided) => ({
            ...provided,
            color: isDarkMode ? "#6e7681" : "#9CA3AF",
            fontSize: "14px",
            fontWeight: "400",
        }),
        control: (provided, state) => ({
            ...CustomSelectStyles.control(provided, state),
            backgroundColor: state.isDisabled 
                ? (isDarkMode ? '#0d1117' : '#f9fafb')
                : (isDarkMode ? '#0d1117' : '#ffffff'),
            borderColor: error ? '#f56565' : (isDarkMode ? (state.isFocused ? '#30363d' : '#30363d') : (state.isFocused ? '#cfcfcf' : '#e5e7eb')),
            boxShadow: error ? '0 0 0 1px #f56565' : state.isFocused ? (isDarkMode ? '0 0 0 1px #30363d' : '0 0 0 1px #cfcfcf') : 'none',
            color: state.isDisabled 
                ? (isDarkMode ? '#6e7681' : '#9ca3af')
                : (isDarkMode ? '#c9d1d9' : '#374151'),
            paddingRight: '8px', // Consistent padding to move arrow away from right edge
            opacity: state.isDisabled ? 0.6 : 1,
            cursor: state.isDisabled ? 'not-allowed' : 'default',
            '&:hover': {
                borderColor: state.isDisabled 
                    ? (isDarkMode ? '#30363d' : '#e5e7eb')
                    : (error ? '#f56565' : (isDarkMode ? '#30363d' : '#cfcfcf'))
            }
        }),
        singleValue: (provided, state) => ({
            ...provided,
            color: state.isDisabled 
                ? (isDarkMode ? '#6e7681' : '#9ca3af')
                : (isDarkMode ? '#c9d1d9' : '#374151'),
        }),
        indicatorsContainer: (provided) => ({
            ...CustomSelectStyles.indicatorsContainer(provided),
            paddingLeft: "8px", // Add left padding to separate from value
        }),
        dropdownIndicator: (provided, state) => ({
            ...CustomSelectStyles.dropdownIndicator(provided),
            padding: "8px 8px", // Standard padding, container handles spacing
            color: state.isDisabled 
                ? (isDarkMode ? '#6e7681' : '#9ca3af')
                : (isDarkMode ? '#8b949e' : '#6b7280'),
        }),
        option: (provided, state) => ({
            ...provided,
            marginTop: "4px",
            marginBottom: "4px",
            height: "36px",
            width: "100%",
            backgroundColor: state.isSelected 
                ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                : state.isFocused 
                    ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                    : (isDarkMode ? '#0d1117' : '#fff'),
            color: isDarkMode ? '#c9d1d9' : '#374151',
            '&:hover': {
                backgroundColor: isDarkMode ? '#1c2128' : '#f3f4f6',
            }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
            borderColor: isDarkMode ? '#30363d' : '#e5e7eb',
        }),
        menuList: (provided) => ({
            ...provided,
            backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
        }),
    };

    return (
        <div className={`relative w-full ${margin} mb-4`}>
            {label && (
                <label className="text-sm font-bold block text-fontColor dark:text-[#c9d1d9] mb-2">
                    {label}
                    {isRequired && showRequiredIndicator && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                </label>
            )}
            <div className={error ? "ring-2 ring-red-500 dark:ring-red-400 rounded-md" : ""}>
                <Select
                options={options}
                value={selectedOption}
                onChange={handleChange}
                placeholder={placeholder}
                styles={styles || customStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={disabled}
                menuPlacement={menuPlacement}
                isSearchable={false}
                theme={(theme) => ({
                    ...theme,
                    colors: {
                        ...theme.colors,
                        primary: '#3b82f6',
                        primary75: '#60a5fa',
                        primary50: '#93c5fd',
                        primary25: '#dbeafe',
                        neutral0: isDarkMode ? '#0d1117' : '#ffffff',
                        neutral5: isDarkMode ? '#161b22' : '#f9fafb',
                        neutral10: isDarkMode ? '#1c2128' : '#f3f4f6',
                        neutral20: isDarkMode ? '#30363d' : '#e5e7eb',
                        neutral30: isDarkMode ? '#484f58' : '#d1d5db',
                        neutral40: isDarkMode ? '#6e7681' : '#9ca3af',
                        neutral50: isDarkMode ? '#8b949e' : '#6b7280',
                        neutral60: isDarkMode ? '#b1bac4' : '#4b5563',
                        neutral70: isDarkMode ? '#c9d1d9' : '#374151',
                        neutral80: isDarkMode ? '#f0f6fc' : '#1f2937',
                        neutral90: isDarkMode ? '#ffffff' : '#111827',
                    },
                })}
            />
            </div>
            {error && <p className="text-red-500 dark:text-red-400 text-xs mt-1 font-medium">{error}</p>}
        </div>
    );
};

export default CustomSelect;
